import * as api from "../../api/digitalPayAPI";
import {
  formatKurus,
  parseKurus,
  BALANCE_TYPES,
  BALANCE_STANDARD,
  balanceTypeLabel,
} from "./constants";
import { isSuperAdminUser } from "./roleUtils";

// Route -> varlık anahtarı.
export const ENTITY_BY_PATH = {
  "/auth-companies": "authCompany",
  "/firms": "firm",
  "/departments": "department",
  // Geçici olarak kapalı: panel oluşmadığı için LimitDefinition liste isteği atılmaz.
  // "/limit-definitions": "limitDefinition",
};

const toOptions = (rows, idKey, labelKey) =>
  rows.map((r) => ({ value: r[idKey], label: r[labelKey] }));

const nameOf = (rows, idKey, labelKey, id) =>
  rows.find((r) => r[idKey] === id)?.[labelKey] ?? id ?? "";

const kurusColumn = (field, headerName) => ({
  field,
  headerName,
  flex: 1,
  minWidth: 110,
  valueFormatter: (value) => formatKurus(value),
});

const kurusField = (name, label) => ({
  name,
  label,
  type: "kurus",
  parse: parseKurus, // form -> API (kuruş)
  format: formatKurus, // API (kuruş) -> form
  default: "",
  helperText: "₺",
});

/**
 * lookups: { firms, departments, authCompanies } — select alanlarını ve
 * kolonlardaki isim çözümlemesini beslemek için DigitalPay bir kez çeker.
 */
export function buildEntityConfig(key, lookups) {
  const { firms = [], departments = [], authCompanies = [] } = lookups;
  const canSelectFirm = isSuperAdminUser();
  const assignedFirmId = canSelectFirm ? "" : (firms[0]?.idfirm ?? "");

  const configs = {
    authCompany: {
      title: "Çatı Şirket",
      listTitle: "Kayıtlı Şirketler",
      sub: "Yeni şirket ekleyebilir veya mevcut şirket bilgilerini düzenleyebilirsiniz.",
      idKey: "idauthCompany",
      api: {
        list: api.getAuthCompanies,
        create: api.createAuthCompany,
        update: api.updateAuthCompany,
        remove: api.deleteAuthCompany,
      },
      columns: [
        { field: "name", headerName: "Ad", flex: 1, minWidth: 160 },
        { field: "companyCode", headerName: "Şirket Kodu", flex: 1, minWidth: 120 },
        { field: "mail", headerName: "E-posta", flex: 1, minWidth: 180 },
      ],
      fields: [
        { name: "name", label: "Şirket Adı", type: "text", required: true },
        { name: "companyCode", label: "Şirket Kodu", type: "text", required: true },
        { name: "mail", label: "E-posta", type: "text" },
      ],
    },

    firm: {
      title: "Firma",
      listTitle: "Kayıtlı Firmalar",
      idKey: "idfirm",
      api: {
        list: api.getFirms,
        create: api.createFirm,
        update: api.updateFirm,
        remove: api.deleteFirm,
      },
      columns: [
        { field: "name", headerName: "Ad", flex: 1, minWidth: 140 },
        { field: "title", headerName: "Unvan", flex: 1, minWidth: 170 },
        { field: "mail", headerName: "E-posta", flex: 1, minWidth: 160 },
        {
          field: "authCompany_idauthCompany",
          headerName: "Çatı Şirket",
          flex: 1,
          minWidth: 140,
          valueGetter: (value) => nameOf(authCompanies, "idauthCompany", "name", value),
        },
      ],
      fields: [
        { name: "name", label: "Firma Adı", type: "text", required: true },
        { name: "title", label: "Unvan", type: "text" },
        { name: "mail", label: "E-posta", type: "text" },
        {
          name: "authCompany_idauthCompany",
          label: "Çatı Şirket",
          type: "select",
          options: toOptions(authCompanies, "idauthCompany", "name"),
          required: true,
        },
        {
          name: "isCardNoDecimal",
          label: "Kart No Ondalık",
          type: "select",
          options: [
            { value: 0, label: "Hayır" },
            { value: 1, label: "Evet" },
          ],
          default: 0,
        },
      ],
    },

    department: {
      title: "Departman",
      listTitle: "Kayıtlı Departmanlar",
      useDialog: true,
      addLabel: "Departman Ekle",
      idKey: "iddepartment",
      api: {
        list: api.getDepartments,
        create: api.createDepartment,
        update: api.updateDepartment,
        remove: api.deleteDepartment,
      },
      columns: [
        // Personel toplu yükleme şablonu DepartmanId istiyor; ID listede görünsün.
        { field: "iddepartment", headerName: "ID", width: 70 },
        { field: "name", headerName: "Ad", flex: 1, minWidth: 140 },
        {
          field: "type",
          headerName: "Tip",
          flex: 1,
          minWidth: 140,
          valueFormatter: (value) => balanceTypeLabel(value),
        },
        ...(canSelectFirm
          ? [
              {
                field: "firm_idfirm",
                headerName: "Firma",
                flex: 1,
                minWidth: 140,
                valueGetter: (value) => nameOf(firms, "idfirm", "name", value),
              },
            ]
          : []),
      ],
      fields: [
        { name: "name", label: "Departman Adı", type: "text", required: true },
        {
          name: "type",
          label: "Tip",
          type: "select",
          options: BALANCE_TYPES,
          default: BALANCE_STANDARD,
        },
        {
          name: "firm_idfirm",
          label: "Firma",
          type: "select",
          options: toOptions(firms, "idfirm", "name"),
          required: true,
          default: assignedFirmId,
          disabled: !canSelectFirm,
        },
      ],
    },

    limitDefinition: {
      title: "Limit Tanımı",
      listTitle: "Kayıtlı Limit Tanımları",
      sub: "Departman bazında standart ve İSG hakedişi.",
      idKey: "idlimitDefinition",
      api: {
        list: api.getLimitDefinitions,
        create: api.createLimitDefinition,
        update: api.updateLimitDefinition,
        remove: api.deleteLimitDefinition,
      },
      columns: [
        { field: "idlimitDefinition", headerName: "ID", width: 70 },
        {
          field: "department_iddepartment",
          headerName: "Departman",
          flex: 1,
          minWidth: 140,
          valueGetter: (value) => nameOf(departments, "iddepartment", "name", value),
        },
        kurusColumn("baseAllowance", "Hakediş"),
        kurusColumn("rolledOverAmount", "Devreden"),
        kurusColumn("isgAllowance", "İSG Hakediş"),
        // Swagger'daki yazım hatası bilerek korunuyor: isgRolledOverAmouth
        kurusColumn("isgRolledOverAmouth", "İSG Devreden"),
        {
          field: "isActive",
          headerName: "Aktif",
          width: 80,
          valueGetter: (value) => (value === 1 ? "✓" : "—"),
        },
      ],
      fields: [
        {
          name: "department_iddepartment",
          label: "Departman",
          type: "select",
          options: toOptions(departments, "iddepartment", "name"),
          required: true,
        },
        // TODO(backend): timePeriodType değerlerinin anlamı bilinmiyor.
        { name: "timePeriodType", label: "Periyot Tipi", type: "number", default: 1 },
        { name: "timePeriodTime", label: "Periyot Süresi (gün)", type: "number", default: 30 },
        kurusField("baseAllowance", "Standart Hakediş"),
        kurusField("rolledOverAmount", "Devreden"),
        { name: "isgTimePeriodType", label: "İSG Periyot Tipi", type: "number", default: 1 },
        { name: "isgTimePeriodTime", label: "İSG Periyot Süresi (gün)", type: "number", default: 30 },
        kurusField("isgAllowance", "İSG Hakediş"),
        kurusField("isgRolledOverAmouth", "İSG Devreden"),
        {
          name: "isActive",
          label: "Aktif",
          type: "select",
          options: [
            { value: 1, label: "Evet" },
            { value: 0, label: "Hayır" },
          ],
          default: 1,
        },
      ],
    },
  };

  return configs[key];
}
